import React from 'react';

// ── Shared style utilities for Digital Xtension CRM ──────────
// All colors reference CSS variables set by BrandContext
// so that branding changes apply site-wide instantly

export const C = {
  primary:      'var(--color-primary)',
  primaryLight: 'var(--color-primary-light)',
  primaryHover: 'var(--color-primary-hover)',
  dark:         'var(--color-dark)',
  white:        '#ffffff',
  bg:           '#f5f7fa',
  border:       '#e5e7eb',
  text:         '#374151',
  muted:        '#6b7280',
  faint:        '#9ca3af',
  success:      '#22C55E',
  danger:       '#EF4444',
  warning:      '#F59E0B',
  purple:       '#7C3AED',
  green:        '#10B981',
};

// ── Reusable button style ────────────────────────────────────
export const btnStyle = (variant = 'primary', size = 'md', extra = {}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: size === 'sm' ? '5px 10px' : size === 'lg' ? '11px 20px' : '8px 16px',
  borderRadius: 8,
  border:
    variant === 'outline' ? `1px solid var(--color-primary)` :
    variant === 'ghost'   ? '1px solid #e5e7eb' :
    variant === 'danger'  ? 'none' : 'none',
  background:
    variant === 'primary' ? 'var(--color-primary)' :
    variant === 'danger'  ? '#EF4444' : 'transparent',
  color:
    variant === 'primary' || variant === 'danger' ? '#fff' :
    variant === 'ghost'   ? '#374151' : 'var(--color-primary)',
  fontSize: size === 'sm' ? 11 : size === 'lg' ? 14 : 13,
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'opacity 0.15s, background 0.15s',
  ...extra,
});

// ── Tag / badge ──────────────────────────────────────────────
export const tagStyle = (color = 'var(--color-primary)') => ({
  background: color === 'var(--color-primary)'
    ? 'var(--color-primary-light)'
    : `${color}18`,
  color,
  borderRadius: 20,
  padding: '2px 10px',
  fontSize: 11,
  fontWeight: 500,
  display: 'inline-block',
});

// ── Card ─────────────────────────────────────────────────────
export const cardStyle = (extra = {}) => ({
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e5e7eb',
  padding: '20px 24px',
  ...extra,
});

// ── Input ────────────────────────────────────────────────────
export const inputStyle = (extra = {}) => ({
  width: '100%',
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid #d1d5db',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
  ...extra,
});

// ── Avatar ───────────────────────────────────────────────────
export const avatarStyle = (size = 32, bg = 'var(--color-primary)') => ({
  width: size,
  height: size,
  borderRadius: '50%',
  background: bg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontSize: size * 0.35,
  fontWeight: 700,
  flexShrink: 0,
});

// ── Metric card ──────────────────────────────────────────────
export const metricStyle = () => ({
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e5e7eb',
  padding: '16px 20px',
});

// ── Notification toast ───────────────────────────────────────
export const toastStyle = (type = 'success') => ({
  position: 'fixed',
  top: 16,
  right: 16,
  zIndex: 1000,
  background: type === 'error' ? '#EF4444' : '#22C55E',
  color: '#fff',
  padding: '10px 18px',
  borderRadius: 10,
  fontSize: 13,
  fontWeight: 500,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
});

// ── Reusable React components ────────────────────────────────

export const Btn = ({ children, variant = 'primary', size = 'md', disabled, style: extra = {}, ...props }) =>
  <button
    disabled={disabled}
    style={{ ...btnStyle(variant, size), opacity: disabled ? 0.6 : 1, cursor: disabled ? 'not-allowed' : 'pointer', ...extra }}
    {...props}
  >
    {children}
  </button>;

export const Tag = ({ label, color }) =>
  <span style={tagStyle(color)}>{label}</span>;

export const Input = ({ label, style: extra = {}, ...props }) =>
  <div>
    {label && <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4, display: 'block' }}>{label}</label>}
    <input style={{ ...inputStyle(), ...extra }} {...props} />
  </div>;

export const Card = ({ title, icon, children, extra: headerExtra, style: cardExtra = {} }) =>
  <div style={{ ...cardStyle(), ...cardExtra }}>
    {title && (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {icon && <i className={`ti ${icon}`} style={{ fontSize: 18, color: 'var(--color-primary)' }} />}
          <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--color-dark)' }}>{title}</span>
        </div>
        {headerExtra}
      </div>
    )}
    {children}
  </div>;

export const Toast = ({ msg, type }) =>
  msg ? <div style={toastStyle(type)}>{msg}</div> : null;

export const Spinner = ({ size = 32 }) =>
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
    <div style={{ width: size, height: size, border: '3px solid #e5e7eb', borderTop: '3px solid var(--color-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
  </div>;

export const initials = (name = '') =>
  name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

export const useNotify = () => {
  const [notification, setNotification] = React.useState(null);
  const notify = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3500);
  };
  return [notification, notify];
};
